import { useEffect } from 'react'
import { isAxiosError, isPortalApiError } from '@afterpaytouch/portal-api/utils'
import { USER_JOURNEY_NAMES } from './constants'
import { useUserJourneyContext } from './UserJourneyContext'

export const NETWORK_SIGNALS = {
  CLIENT: 'client-network-error',
  SERVER: 'server-network-error',
  UNKNOWN: 'unknown-network-error',
} as const

export type NetworkSignal = {
  signalType: 'friction' | 'frustration'
  signal: string
}

const JOURNEY_RELEVANT_PATHS = [
  '/portal/consumers/checkout',
  '/portal/consumers/auth',
  '/portal/consumers/account',
  '/portal/consumers/identities',
  '/portal/consumers/apple-pay-sessions',
  '/portal/consumers/apple-pay-token',
  '/portal/consumers/threeDS',
  '/portal/consumers/registrations',
  '/portal/consumers/emails/lookup',
  '/portal/consumers/shipping-addresses',
  '/portal/force-password-reset',
  '/portal/consumers/efa-sms',
  '/portal/consumers/efa-verify-code',
]

const JOURNEY_IRRELEVANT_PATHS = ['/portal/consumers/auth/logout']

const matchesPath = (url: string, path: string): boolean => url === path || url.startsWith(`${path}/`)

export const isJourneyRelevantRequest = (url: string | undefined): boolean =>
  url !== undefined && JOURNEY_RELEVANT_PATHS.some((path) => matchesPath(url, path)) && !JOURNEY_IRRELEVANT_PATHS.some((path) => matchesPath(url, path))

export const classifyNetworkError = (status: number | undefined): NetworkSignal => {
  if (status === undefined || status === 401 || status === 408 || status === 429) {
    return { signalType: 'friction', signal: NETWORK_SIGNALS.CLIENT }
  }
  if (status >= 400 && status < 500) {
    return { signalType: 'frustration', signal: NETWORK_SIGNALS.CLIENT }
  }
  if (status >= 500 && status < 600) {
    return { signalType: 'frustration', signal: NETWORK_SIGNALS.SERVER }
  }
  return { signalType: 'frustration', signal: NETWORK_SIGNALS.UNKNOWN }
}

type NetworkSignalHandler = (signal: NetworkSignal) => void

let networkSignalHandler: NetworkSignalHandler | undefined

export const setNetworkSignalHandler = (handler: NetworkSignalHandler | undefined): void => {
  networkSignalHandler = handler
}

export const reportJourneyNetworkError = (error: unknown): void => {
  if (!(error instanceof Error) || !isAxiosError(error)) {
    return
  }
  if (error.code === 'ERR_CANCELED') {
    return
  }
  const retryCount = error.config?.['axios-retry']?.retryCount ?? 0
  if (retryCount > 0 || !isJourneyRelevantRequest(error.config?.url)) {
    return
  }
  const status = error.response?.status
  if (status !== undefined && status !== 401 && status !== 408 && status < 500 && isPortalApiError(error.response?.data)) {
    return
  }
  try {
    networkSignalHandler?.(classifyNetworkError(status))
  } catch {
    // A faulty handler must not break the interceptor's error flow
  }
}

export const reportJourneyNetworkFailure = (url: string | undefined, status: number | undefined): void => {
  if (!isJourneyRelevantRequest(url)) {
    return
  }
  try {
    networkSignalHandler?.(classifyNetworkError(status))
  } catch {
    // Instrumentation must not alter the caller's error flow
  }
}

export const useJourneyNetworkSignals = (): void => {
  const { addFriction, addFrustration } = useUserJourneyContext()

  useEffect(() => {
    setNetworkSignalHandler(({ signalType, signal }) => {
      if (signalType === 'friction') {
        addFriction(USER_JOURNEY_NAMES.CHECKOUT, signal)
      } else {
        addFrustration(USER_JOURNEY_NAMES.CHECKOUT, signal)
      }
    })
    return () => {
      setNetworkSignalHandler(undefined)
    }
  }, [addFriction, addFrustration])
}
